import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Stack,
  Typography,
  Paper,
  Tooltip,
  MenuItem,
  Select,
  InputLabel,
  OutlinedInput,
  FormControl,
  FormControlLabel,
  Checkbox,
  SelectChangeEvent
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import axios from '../../../../services/ApiService';
import LoadingSpinner from '../../../support/LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { paths } from '../../../../utils/paths';
import { Link } from 'react-router-dom';
import { Iconify } from '../../../core/iconify';
import Swal from 'sweetalert2'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlineIcon from '@mui/icons-material/EditOutlined';
import AddIcon from '@mui/icons-material/Add';
import { ProgramData } from '../../../types/ProgramTypes';
import RefreshOutlineIcon from '@mui/icons-material/RefreshOutlined';
import { BVTConstants } from '../../../../utils/BVTConstants';
import toast from 'react-hot-toast';
import { CompanyCategory } from '../../../types/DataTypes';

export default function ViewPrograms() {
  const [programs, setPrograms] = useState<ProgramData[]>([]);
  const [filteredPrograms, setFilteredPrograms] = useState<ProgramData[]>([]);
  const [categories, setCategories] = useState<CompanyCategory[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<number | ''>('');
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>('all');
  const [orderBy, setOrderBy] = useState<string>('asc');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [includeInactive, setIncludeInactive] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPrograms();
    fetchCategories();
  }, [includeInactive]);

  const fetchPrograms = async () => {
    setLoading(true);
    setPrograms([]);
    try {
      const response = await axios.post('/getPrograms', includeInactive);
      if (Array.isArray(response.data)) {
        setPrograms(response.data);
      }
    } catch (error) {
      setError('Failed to load programs.');
      console.error('Error fetching programs.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    setCategories([]);
    try {
      const response = await axios.get('/getCompanyCategories');
      if (Array.isArray(response.data)) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Error fetching categories.');
    }
  };

  const deleteProgram = async (id: number) => {
    try {
      setLoading(true);
      await axios.delete(`/deleteProgram/${id}`);
      toast.success('Program archived successfully');
      fetchPrograms();
    } catch (error) {
      console.error('Error deleting program.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (program: ProgramData) => {
    Swal.fire({title: 'Archive Program ?', 
      text: 'Are you sure you want to archive the program ' + program.programName + '?', 
      icon: 'question', 
      confirmButtonText: 'Archive', 
      cancelButtonText: 'Cancel', 
      showCancelButton: true }
    ).then((result) => {
      if(result['isConfirmed']){
        deleteProgram(program.id);
      }
    })
  };

  const changeStatus = async(req: any) => {
    try {
      setLoading(true);
      const response = await axios.post("/updateProgramStatus", req);
      if(response.status === 200){
        toast.success('Program updated successfully');
        fetchPrograms();
      }
    } catch (error) {
      console.error('Error updating program status.');
    } finally {
      setLoading(false);
    }
  }

  const handleStatusChangeClick = (program: ProgramData) => {
    const activeText: string = program.status === BVTConstants.ACTIVE ? 'Deactivate' : 'Activate';
    Swal.fire({title: activeText + ' Program ?', 
      text: 'Do you want to '+ activeText +' the Program ' + program.programName + '?', 
      icon: 'question', 
      confirmButtonText: activeText, 
      cancelButtonText: 'Cancel', 
      showCancelButton: true }
    ).then((result) => {
      if(result['isConfirmed']){
        const req = {id: program.id, status: program.status === BVTConstants.ACTIVE ? BVTConstants.INACTIVE : BVTConstants.ACTIVE}
        changeStatus(req);
      }
    })
  };

  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSubscriptionFilterChange = (e: SelectChangeEvent<string>) => {
    setSubscriptionFilter(e.target.value);
  };

  const handleCategoryChange = (e: SelectChangeEvent<number | string>) => {
    setSelectedCategory(e.target.value as number | '');
  };

  const handleIncludeInactiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIncludeInactive(e.target.checked);
  };

  useEffect(() => {
    const filteredList = programs
    .filter((program) => {
      const programNameMatch = program.programName.toLowerCase().includes(searchTerm.toLowerCase());
      const categoryMatch = selectedCategory === '' || 
        program.categories?.some(category => category.catId === selectedCategory);
      const subscriptionMatch = subscriptionFilter === 'all' || 
        (subscriptionFilter === 'subscription' && program.hasSubscription) ||
        (subscriptionFilter === 'no-subscription' && !program.hasSubscription);
      return programNameMatch && categoryMatch && subscriptionMatch;
    })
    .sort((a, b) => {
      if (orderBy === 'asc') {
        return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
      } else {
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }
    });
    setFilteredPrograms(filteredList);
  }, [subscriptionFilter, searchTerm, selectedCategory, programs])

  return (
    <>
      <Helmet>
        <title>BVT Admin - View Programs</title>
      </Helmet>
      <Box sx={{ padding: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight="500">
            View
          </Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button
            variant="contained"
            color="inherit"
            startIcon={<Iconify icon="mingcute:add-line" />}
            component={Link}
            to={paths.admin.programForm}
            sx={{marginRight: 1}}
          >
            New Program
          </Button>
          <Tooltip title="Refresh Programs" arrow>
            <Button
              variant="contained"
              color="inherit"
              onClick={() => fetchPrograms()}
            >
              <RefreshOutlineIcon />
            </Button>
          </Tooltip>
          </Stack>
        </Stack>

        <Box sx={{ marginTop: 2, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <FormControl sx={{ width: '55%' }}>
            <OutlinedInput
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search by Program Name"
            />
          </FormControl>
          <FormControl sx={{ width: '30%' }}>
            <InputLabel>Filter by Category</InputLabel>
            <Select
              value={selectedCategory}
              onChange={handleCategoryChange}
              label="Filter by Category"
            >
              <MenuItem value="">
                <em>All Categories</em>
              </MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.catId} value={category.catId}>
                  {category.catName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ width: '15%' }}>
            <InputLabel>Subscription</InputLabel>
            <Select
              value={subscriptionFilter}
              onChange={handleSubscriptionFilterChange}
              label="Subscription"
            >
              <MenuItem value="all">All Programs</MenuItem>
              <MenuItem value="subscription">Yes</MenuItem>
              <MenuItem value="no-subscription">No</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ marginTop: -0.5, marginBottom: 1, marginLeft: 1, display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeInactive}
                onChange={handleIncludeInactiveChange}
                color="primary"
              />
            }
            label="Include Inactive Programs"
          />
        </Box>

        {loading ? (
          <LoadingSpinner loading={loading} />
        ) : error ? (
          <Typography variant="h6" color="error" textAlign="center">
            {error}
          </Typography>
        ) : (
          <Grid container spacing={2} sx={{ marginTop: 2 }}>
            {filteredPrograms.length > 0 ? (
              filteredPrograms.map((program) => (
                <Grid item xs={12} sm={6} key={program.id} sx={{ marginTop: -1 }}>
                  <Card
                    sx={{
                      boxShadow: 4,
                      borderRadius: 2,
                      backgroundColor: '#fff',
                      transition: 'transform 0.3s',
                      '&:hover': {
                        transform: 'scale(1.02)',
                      },
                    }}
                  >
                    <CardHeader
                      title={program.programName}
                      subheader={program.description}
                      sx={{
                        backgroundColor: '#f5f5f5',
                        color: program.status === 'ACTIVE' ? 'black' : 'orangered',
                        borderTopLeftRadius: 2,
                        borderTopRightRadius: 2,
                        boxShadow: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                      action={
                        <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                        >
                          { program.status === BVTConstants.ACTIVE ? (
                            <>
                              <Tooltip title="Edit Program" arrow>
                                <Button
                                  variant="outlined"
                                  color="info"
                                  sx={{
                                    padding: '5px',
                                    minWidth: 'auto',
                                    marginRight: 1
                                  }}
                                  onClick={() => {
                                    navigate(paths.admin.programForm, { state: { id: program.id, name: program.programName, desc: program.description } });
                                  }}
                                >
                                  <EditOutlineIcon sx={{ fontSize: 22 }}/>
                                </Button>
                              </Tooltip>
                              <Tooltip title="Add Workflow" arrow>
                                <Button
                                  variant="outlined"
                                  color="success"
                                  sx={{
                                    padding: '5px',
                                    minWidth: 'auto',
                                    marginRight: 1
                                  }}
                                  onClick={() => {
                                    navigate(paths.admin.workflowForm, { state: { programId: program.id, programName: program.programName } });
                                  }}
                                >
                                  <AddIcon sx={{ fontSize: 22 }}/>
                                </Button>
                              </Tooltip>
                            </>
                          ):(
                            <></>
                          )}
                          <Tooltip title="Archive Program" arrow>
                            <Button
                              variant="outlined"
                              color="error"
                              sx={{
                                padding: '5px',
                                minWidth: 'auto'
                              }}
                              onClick={() => handleDeleteClick(program)}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 22 }}/>
                            </Button>
                          </Tooltip>
                        </Box>
                      }
                    />
                    <Divider />
                    <CardContent sx={{ padding: 2 }}>
                      <Stack spacing={1}>
                        <Paper elevation={1} sx={{ padding: 1, borderRadius: 1 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <Typography variant="body2" fontWeight="bold">
                              Number of Workflows:
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" sx={{ marginRight: 3}}>
                              {program.workflows.length}
                            </Typography>
                            {/* <Button
                                variant="outlined"
                                size='small'
                                onClick={() => {
                                  navigate(paths.admin.programForm, { state: { id: program.id, name: program.programName, desc: program.description } });
                                }}
                            >ADD</Button> */}
                          </Box>
                        </Paper>
                        <Paper elevation={1} sx={{ padding: 1, borderRadius: 1 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <Typography variant="body2" fontWeight="bold">
                              Subscription Required:
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" sx={{ marginRight: 3}}>
                              {program.hasSubscription ? 'Yes' : 'No'}
                            </Typography>
                          </Box>
                        </Paper>
                        <Paper elevation={1} sx={{ padding: 1, borderRadius: 1 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <Typography variant="body2" fontWeight="bold">
                              Subscription Cost:
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" sx={{ marginRight: 3}}>
                              {program.hasSubscription ? `${program.amount?.toFixed(2)}/${program.frequency}` : 'N/A'}
                            </Typography>
                          </Box>
                        </Paper>
                      </Stack>
                    </CardContent>
                    <Divider />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: 1,
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant="caption" color="textSecondary">
                          Created At: {new Date(program.createdAt).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Updated At: {new Date(program.updatedAt).toLocaleString()}
                        </Typography>
                      </Box>
                      <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: 1,
                        alignItems: 'center',
                      }}
                      >
                        <Button
                          variant="outlined"
                          sx={{marginRight: 1}}
                          onClick={() => handleStatusChangeClick(program)}
                        >
                          {program.status === BVTConstants.ACTIVE ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            navigate(paths.admin.programDetails, { state: { id: program.id } });
                          }}
                        >
                          View
                        </Button>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography variant="h6" textAlign="center" color="textSecondary">
                  {loading ? 'Loading...' : 'No programs found.'}
                </Typography>
              </Grid>
            )}
          </Grid>
        )}
      </Box>
    </>
  );
}
